import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges, OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  Game,
  GameUser,
  GameVolleyballPosition,
  GameVolleyballPositionLocalization, generateArray, getEnumOptions,
  Team, Tournament,
  VolleyballGameLog, VolleyballGameLogType,
  VolleyballGameStatistic, VolleyballStatisticTypes
} from '@mtgame/core';
import { UntypedFormControl } from '@angular/forms';
import { getHeaderHeight } from '@utils/layout';
import { getLeaguePlayerRoute, getTeamPageRoute } from '@config/routes';

@Component({
  selector: 'mtg-volleyball-game-protocol',
  templateUrl: './volleyball-game-protocol.component.html',
  styleUrls: ['./volleyball-game-protocol.component.scss', '../../game/styles/protocol.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VolleyballGameProtocolComponent implements OnInit, OnChanges {
  @Input()
  users: GameUser[] = [];
  @Input()
  gameStatistic: VolleyballGameStatistic[] = [];
  @Input()
  logs: VolleyballGameLog[] = [];
  @Input()
  team: Team;
  @Input()
  game: Game;
  @Input()
  tournament: Tournament;
  @Input()
  dialog: boolean;

  filteredUsers: GameUser[] = [];
  filteredStatistic: {[key: number]: VolleyballGameStatistic} = {};
  sets: number[] = [];
  positionLocalization = GameVolleyballPositionLocalization;
  positions = getEnumOptions(GameVolleyballPosition);
  setControl = new UntypedFormControl(null);
  positionControl = new UntypedFormControl(null);
  query = new UntypedFormControl();
  headerHeight = getHeaderHeight();

  constructor(
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.setControl.valueChanges
      .subscribe(() => {
        this.filteredStatistic = this.getFilteredStatistic();
        this.cdr.markForCheck();
      });
    this.positionControl.valueChanges
      .subscribe(() => {
        this.filteredUsers = this.getFilteredUsers();
        this.cdr.markForCheck();
      });
    this.query.valueChanges
      .subscribe(() => {
        this.filteredUsers = this.getFilteredUsers();
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.users || changes.logs || changes.gameStatistic) && this.users && this.logs && this.gameStatistic) {
      this.updateProtocol();
    }
  }

  getTotal(key: string): number {
    const userIds = this.filteredUsers.map(u => u.id);
    return Object.keys(this.filteredStatistic)
      .filter(k => userIds.includes(this.filteredStatistic[k].gameUserId))
      .map(k => this.filteredStatistic[k])
      .reduce((acc, item) => acc + (item[key] || 0), 0);
  }

  getUserProfileRoute(gameUser: GameUser): string[] {
    return getLeaguePlayerRoute(gameUser.tournamentTeamUser.leaguePlayer.id);
  }

  getTeamRoute(teamId: number): string[] {
    return getTeamPageRoute(teamId);
  }

  updateProtocol() {
    const setsCount = this.logs.map(l => l.set).filter((v, i, a) => a.indexOf(v) === i).length;
    this.sets = generateArray(setsCount);
    this.filteredStatistic = this.getFilteredStatistic();
    this.filteredUsers = this.getFilteredUsers();
    this.cdr.markForCheck();
  }

  getFilteredUsers() {
    const query = this.query.value;
    const position = +this.positionControl.value;
    if (!query && !position) {
      return this.users;
    }
    return this.users.filter(user => {
      if (query) {
        if (this.game.tournamentId) {
          if (user.tournamentTeamUser.leaguePlayer.shortName.toLowerCase().indexOf(query.toLowerCase) < 0) {
            return false;
          }
        } else if (user.teamUser.user.shortName.toLowerCase().indexOf(query.toLowerCase()) < 0) {
          return false;
        }
      }
      if (position) {
        if (user.teamUser.user.volleyballProfile.position !== position) {
          return false;
        }
      }
      return true;
    });
  }

  getFilteredStatistic() {
    const set = +this.setControl.value;
    if (!set) {
      return this.gameStatistic.reduce((acc, item) => Object.assign(acc, {[item.gameUserId]: item}), {});
    }
    const logs = this.logs.filter(item => item.set === set);
    return (this.gameStatistic || []).map(item => {
      return this.updateGameUserStats(Object.assign(new VolleyballGameStatistic(), item), logs);
    }).reduce((acc, item) => Object.assign(acc, {[item.gameUserId]: item}), {});
  }

  updateGameUserStats(gameStatistic: VolleyballGameStatistic, logs: VolleyballGameLog[]) {
    const userLogs = logs.filter(item => item.gameUserId === gameStatistic.gameUserId);
    const logMapping = {
      serveHits: VolleyballGameLogType.serve_hit,
      serveAces: VolleyballGameLogType.serve_ace,
      serveFaults: VolleyballGameLogType.serve_fault,
      attackSpikes: VolleyballGameLogType.attack_spike,
      attackShots: VolleyballGameLogType.attack_shot,
      attackFaults: VolleyballGameLogType.attack_fault,
      stuffBlocks: VolleyballGameLogType.stuff_block,
      blockRebounds: VolleyballGameLogType.block_rebound,
      blockFaults: VolleyballGameLogType.block_fault,
      receives: VolleyballGameLogType.receive,
      excellentReceives: VolleyballGameLogType.excellent_receive,
      receiveFaults: VolleyballGameLogType.receive_fault,
      serveReceives: VolleyballGameLogType.serve_receive,
      serveReceiveFaults: VolleyballGameLogType.serve_receive_fault,
      excellentServeReceives: VolleyballGameLogType.excellent_serve_receive,
      points: VolleyballGameLogType.point,
      faults: VolleyballGameLogType.fault,
    };
    for (const key of Object.keys(logMapping)) {
      gameStatistic[key] = userLogs.filter(log => log.logType === logMapping[key]).length;
    }
    gameStatistic.points = gameStatistic.points + gameStatistic.attackSpikes + gameStatistic.serveAces + gameStatistic.stuffBlocks;
    gameStatistic.faults = gameStatistic.faults + gameStatistic.attackFaults + gameStatistic.receiveFaults +
      gameStatistic.blockFaults + gameStatistic.serveFaults;
    return gameStatistic;
  }

  getUserStatistic(gameUserId: number, key: string): number {
    return this.filteredStatistic[gameUserId] && this.filteredStatistic[gameUserId][key] || 0;
  }

  get isLight(): boolean {
    if (this.tournament) {
      return this.tournament.settings.volleyballStatisticType === VolleyballStatisticTypes.light;
    }
    return this.game && this.game.volleyballGameConfig && this.game.volleyballGameConfig.statisticType === VolleyballStatisticTypes.light;
  }

  identify(index: number, gameUser: GameUser): number {
    return gameUser.id;
  }
}
